/**
 * useKeyboardInsets — keep the focused field visible above the on-screen keyboard.
 *
 * Cross-platform, dependency-free keyboard-avoidance built on the VisualViewport
 * API (the only primitive that works on iOS Safari / WKWebView, where the
 * `interactive-widget` viewport meta and `env(keyboard-inset-*)` are ignored, and
 * where the Android-native WebView resize is not available).
 *
 * Four jobs:
 *  1. Publish `--keyboard-inset-height` on the document root (<html>) — but ONLY
 *     while a text-editable element is focused AND the surface is touch-like — as
 *     the amount the layout viewport did NOT already shrink for the keyboard
 *     (clientHeight - visualViewport.height - offsetTop, clamped >= 0). It is ~0 on
 *     surfaces that resize the layout themselves (Android native WebView with
 *     adjustResize + IME inset; Chrome-Android web with interactive-widget=
 *     resizes-content) and > 0 on iOS (visual-only shrink) — i.e. exactly "how much
 *     a fixed/sticky element must lift to clear the keyboard". Gating on a focused
 *     editable is what stops iOS Safari's own scroll-driven toolbar-collapse (which
 *     also shrinks the visual viewport) from spuriously lifting footers.
 *  2. Toggle a `data-kb-focus` attribute on <html> while the keyboard is plausibly
 *     OPEN over a focused editable — focus alone is not evidence (Android's back
 *     gesture closes the IME without blurring; hardware keyboards never raise one),
 *     so it additionally requires either a real visual-viewport inset (iOS) or a
 *     shrunken layout viewport vs a rolling max (Android resizes-content, inset ≈ 0).
 *     Pinned bottom chrome uses it to collapse to its primary action while typing
 *     (the wizard footer's secondary-action hide in OnboardingWizardLayout).
 *  3. On focus of an editable inside a q-dialog (or inside a `data-kb-anchor-top`
 *     opt-in container) on a phone, anchor the field to the TOP of its scroller
 *     once the keyboard settles — field on top, its content/suggestion list below,
 *     keyboard at the bottom. Established by the property-create address field;
 *     generalised here so every modal input behaves the same way.
 *  4. On focus of an editable, once the keyboard geometry settles, scroll it to
 *     viewport-center IF it is obscured — but only when a real keyboard inset is
 *     present (K > threshold, i.e. iOS / non-resizing surfaces). On Android-native /
 *     Chrome-web (K≈0) the browser's own scroll-focused-editable-into-view handles
 *     it, so we defer and avoid a double-scroll. The inset is recomputed fresh at
 *     scroll time (not read from a possibly-stale snapshot) so a slow keyboard that
 *     opens after the focus timer still gets corrected. Honors the app's reduced-
 *     motion setting (in-app toggle OR OS), skips inputs inside a fixed overlay that
 *     has no scrollable region of its own (a window scroll can't move a fixed
 *     dialog), and never runs on desktop/pointer-fine surfaces.
 *
 * The var is declared statically as 0px on :root ONLY (never re-declared on body —
 * a body re-declaration would win the cascade and freeze descendants at 0px,
 * defeating the runtime value; this mirrors the --app-font-size precedent, which is
 * likewise :root-only + set via document.documentElement).
 *
 * The document-level focus/viewport listeners are installed globally, but the CSS
 * var and the corrective scroll only act on touch-like surfaces; on desktop the
 * inset stays 0 and no scroll fires. install() is idempotent and returns a teardown;
 * call it once from a boot file.
 */
import { ref, readonly } from 'vue';

const KEYBOARD_OPEN_THRESHOLD_PX = 120; // filters address-bar collapse + rounding noise
const SETTLE_MS = 140; // wait after the last viewport resize before correcting scroll
const FOCUS_SETTLE_MS = 320; // focusin -> keyboard fully up (slide anim ~250-350ms)
const CLEARANCE_MARGIN_PX = 24; // breathing room above the keyboard/footer
const MAX_KEYBOARD_SCALE = 1.01; // above this, the visual-viewport shrink is pinch-zoom, not a keyboard
const TOP_ANCHOR_SETTLE_MS = 350; // focus -> keyboard settled, then anchor the field to the top
const TOP_ANCHOR_REARM_MS = 80; // late keyboard (slow IME): slack just appeared, re-anchor quickly

const keyboardHeight = ref(0);
const isKeyboardOpen = ref(false);

let installed = false;
let teardown: (() => void) | null = null;

function isEditable(el: EventTarget | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    // non-text inputs (button, checkbox, file, ...) don't summon the soft keyboard
    return !['button', 'checkbox', 'radio', 'file', 'submit', 'reset', 'range', 'color', 'hidden', 'image'].includes(type);
  }
  return el.isContentEditable;
}

// Reduced motion = the app's in-app accessibility toggle (data-reduced-motion on
// <html>, set by userSettings.applyReducedMotion) OR the OS media query. Mirrors
// apps/_shared/vue/composables/useSkipToContent.ts so both honour the same signal.
function prefersReducedMotion(): boolean {
  return document.documentElement.hasAttribute('data-reduced-motion')
    || (typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

// Only act on surfaces that raise a soft keyboard: coarse pointer (touch) OR a
// small viewport. Keeps desktop mouse users untouched.
function isTouchLikeSurface(): boolean {
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(pointer: coarse)').matches
    || window.matchMedia('(max-width: 1023px)').matches;
}

// Natural reading order while typing on a phone: field at the TOP of the visible
// area, its content/suggestions below, keyboard at the bottom (Baymard's
// field/list/keyboard sandwich — established by the property-create address field).
// Android's own focus reveal is MINIMAL and can leave a field low in the
// keyboard-shrunken viewport, where dropdowns flip ABOVE the anchor and pinned
// chrome crowds it. Applies automatically to every editable inside a q-dialog
// (the card is its own scroller with `scroll-padding-top` breathing room in
// app.scss; Quasar's body scroll-lock keeps the page behind from moving), and to
// page fields that opt in via a `data-kb-anchor-top` attribute on the field or an
// ancestor. Phones only (<600px): larger viewports have room below the field.
function topAnchorTarget(el: HTMLElement): HTMLElement | null {
  if (typeof window.matchMedia !== 'function') return null;
  if (!window.matchMedia('(max-width: 599.98px)').matches) return null;
  if (!el.closest('.q-dialog') && !el.closest('[data-kb-anchor-top]')) return null;
  // Anchor the whole field (label + control) when the editable sits in a q-field.
  return (el.closest('.q-field') as HTMLElement | null) ?? el;
}

// A window/document scroll cannot move a position:fixed element, so scrolling for
// an input inside a fixed overlay that has NO scroll region of its own (e.g. a
// short seamless CommandPalette) is a pointless background jump. But a normal
// Quasar q-dialog gives `.q-dialog__inner` a `position:fixed` class while its inner
// content div is genuinely scrollable — there, scrollIntoView correctly scrolls
// that region, so we must NOT skip. Rule: walk up; if a scrollable ancestor is
// found before any fixed boundary, allow; if a fixed boundary is hit first, skip.
function shouldSkipCorrectiveScroll(el: HTMLElement): boolean {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node);
    const canScroll = (style.overflowY === 'auto' || style.overflowY === 'scroll')
      && node.scrollHeight > node.clientHeight;
    if (canScroll) return false; // a real scroll region exists — let scrollIntoView use it
    if (style.position === 'fixed') return true; // fixed with no scroll slack — skip
    node = node.parentElement;
  }
  return false;
}

export function useKeyboardInsets() {
  function install(): () => void {
    if (installed) return teardown ?? (() => {});
    if (typeof window === 'undefined' || !window.visualViewport) {
      // SSR or no VisualViewport support — no-op (scroll-into-view degrades to the
      // browser's own focus behavior; the CSS var stays its static 0px).
      return () => {};
    }
    installed = true;
    const vv = window.visualViewport;
    const root = document.documentElement;

    let focusedEditable: HTMLElement | null = null;
    let rafId: number | null = null;
    let scrollTimer: ReturnType<typeof setTimeout> | null = null;
    let blurTimer: ReturnType<typeof setTimeout> | null = null;
    let anchorTimer: ReturnType<typeof setTimeout> | null = null;
    let wasKbLikelyOpen = false;
    // Rolling max of the layout-viewport height, used to detect the Android
    // resizes-content path where the keyboard shrinks the LAYOUT viewport and the
    // visual-viewport inset therefore stays ~0. Reset on orientation change so a
    // rotate doesn't leave a stale (taller) max that reads as "keyboard open".
    let maxClientHeight = 0;

    const computeInset = (): number => {
      // Pinch-zoom also shrinks visualViewport.height (~layoutHeight / scale) while
      // clientHeight (the layout viewport) stays fixed, so a zoomed-in page would
      // masquerade as a keyboard and mis-lift every consumer + fire a corrective
      // scroll. A real keyboard-open resize happens at scale ~1, so while the user is
      // zoomed in we report no inset and defer to the browser's own focus scrolling —
      // important for the low-vision users who rely on pinch-zoom.
      if (vv.scale > MAX_KEYBOARD_SCALE) return 0;
      // Layout viewport height is stable; visual viewport shrinks under the keyboard.
      // offsetTop covers the iOS case where the visual viewport is vertically offset.
      const gap = root.clientHeight - vv.height - vv.offsetTop;
      return gap > 0 ? Math.round(gap) : 0;
    };

    const publish = () => {
      rafId = null;
      // A removed-from-DOM element fires no focusout (e.g. Enter on the last wizard
      // field navigates and unmounts the input) — invalidate before trusting it.
      if (focusedEditable && !focusedEditable.isConnected) focusedEditable = null;
      // Only expose a non-zero inset while an editable is focused on a touch-like
      // surface — otherwise iOS Safari chrome collapse on scroll would masquerade as
      // keyboard height and lift footers. (Pinch-zoom is filtered inside computeInset.)
      const inset = focusedEditable && isTouchLikeSurface() ? computeInset() : 0;
      keyboardHeight.value = inset;
      isKeyboardOpen.value = inset > KEYBOARD_OPEN_THRESHOLD_PX;
      root.style.setProperty('--keyboard-inset-height', `${inset}px`);

      // data-kb-focus: "the soft keyboard is plausibly OPEN over a focused editable" —
      // the signal pinned bottom chrome uses to collapse to its primary action. Focus
      // alone is NOT enough (Android's back gesture closes the IME without blurring, and
      // a hardware-keyboard user never raises one), so require keyboard-open evidence:
      // either a real visual-viewport inset (iOS) or a shrunken LAYOUT viewport vs a
      // closed-keyboard baseline (Android resizes-content / native adjustResize, where
      // inset ≈ 0). The keyboard-close resize re-runs publish() and self-clears the
      // attribute. Baseline discipline: while NOTHING is focused the keyboard cannot be
      // open, so any unfocused height IS the closed-keyboard baseline — assign, don't
      // roll. Rolling only while focused; a grow-only max would let a non-keyboard
      // shrink (Android split-screen, browser page zoom — vv.scale stays 1 there, so
      // the pinch-zoom guard never sees it) permanently masquerade as an open keyboard.
      if (focusedEditable === null) {
        maxClientHeight = root.clientHeight;
      } else {
        maxClientHeight = Math.max(maxClientHeight, root.clientHeight);
      }
      const layoutShrunk = root.clientHeight < maxClientHeight - KEYBOARD_OPEN_THRESHOLD_PX;
      const kbLikelyOpen = focusedEditable !== null && isTouchLikeSurface()
        && (inset > KEYBOARD_OPEN_THRESHOLD_PX || layoutShrunk);
      if (kbLikelyOpen) {
        root.setAttribute('data-kb-focus', '');
      } else {
        root.removeAttribute('data-kb-focus');
      }
      // Slow-IME re-anchor: a cold-starting keyboard can open AFTER the 350ms focus
      // anchor already fired against an unpadded card (it stalls at max scroll).
      // The while-typing card slack appears with data-kb-focus, so on its rising
      // edge re-run the anchor once — quickly, the geometry is already settled.
      // Skipped while a focus-scheduled anchor is still pending (it will land with
      // the slack present).
      if (kbLikelyOpen && !wasKbLikelyOpen && focusedEditable && anchorTimer === null) {
        scheduleTopAnchor(focusedEditable, TOP_ANCHOR_REARM_MS);
      }
      wasKbLikelyOpen = kbLikelyOpen;
    };

    const schedulePublish = () => {
      if (rafId !== null) return; // coalesce a burst of resize/scroll into one frame
      rafId = requestAnimationFrame(publish);
    };

    const maybeScrollIntoView = (el: HTMLElement) => {
      if (!isTouchLikeSurface()) return;
      // Recompute the inset fresh rather than trusting the last published snapshot —
      // a slow keyboard may open after the focus timer fired. On Android native /
      // Chrome web the layout already shrank (inset≈0) and the browser scrolls the
      // field itself, so we defer; on iOS (inset>0) we correct.
      if (computeInset() <= KEYBOARD_OPEN_THRESHOLD_PX) return;
      // Top-anchored fields (dialog inputs / opt-ins) must never be CENTER-corrected:
      // on iOS this fires ~390-440ms after focus — right after (or mid-animation of)
      // the 350ms block:'start' anchor — and would drag the field back to the middle.
      // While the anchor is pending, stand down entirely; afterwards, re-assert the
      // anchor position instead of centering.
      const anchorTarget = topAnchorTarget(el);
      if (anchorTarget && anchorTimer !== null) return;
      if (shouldSkipCorrectiveScroll(el)) return; // its overlay owns scrolling / can't move
      const rect = el.getBoundingClientRect();
      const visibleTop = vv.offsetTop;
      const visibleBottom = vv.offsetTop + vv.height;
      const obscured = rect.bottom > visibleBottom - CLEARANCE_MARGIN_PX
        || rect.top < visibleTop + CLEARANCE_MARGIN_PX;
      if (!obscured) return; // already comfortably visible — don't yank it
      (anchorTarget ?? el).scrollIntoView({
        block: anchorTarget ? 'start' : 'center',
        inline: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    // Anchor a dialog/opt-in field to the top of its scroller once the keyboard has
    // settled. Guards at fire time: node still attached and still focused (a removed
    // node fires no focusout — same discipline as publish()). The card/document
    // scroller's scroll-padding-top provides the breathing room.
    const scheduleTopAnchor = (el: HTMLElement, delayMs = TOP_ANCHOR_SETTLE_MS) => {
      const target = topAnchorTarget(el);
      if (!target) return;
      if (anchorTimer) clearTimeout(anchorTimer);
      anchorTimer = setTimeout(() => {
        anchorTimer = null;
        if (!el.isConnected || document.activeElement !== el) return;
        target.scrollIntoView({
          block: 'start',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }, delayMs);
    };

    const onFocusIn = (e: FocusEvent) => {
      // Unconditional re-publish: focus moving to a NON-editable (a button after a
      // route change, say) is a healing opportunity for a stale focusedEditable whose
      // node was unmounted without a focusout (see the isConnected check in publish).
      schedulePublish();
      if (!isEditable(e.target)) return;
      focusedEditable = e.target;
      scheduleTopAnchor(e.target);
      schedulePublish();
      // Wait for the keyboard to open + its slide animation to settle, then correct
      // once. The viewport-resize path below is the reliable trigger; this is an
      // earlier supplementary attempt (both recompute the inset fresh).
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (focusedEditable) maybeScrollIntoView(focusedEditable);
      }, FOCUS_SETTLE_MS);
    };

    const onFocusOut = () => {
      // Defer: focus may be moving to another editable (keyboard stays up). If the
      // new focus target is not editable, zero the inset. Tracked so teardown can
      // cancel a pending re-publish.
      if (blurTimer) clearTimeout(blurTimer);
      blurTimer = setTimeout(() => {
        blurTimer = null;
        focusedEditable = isEditable(document.activeElement)
          ? (document.activeElement as HTMLElement)
          : null;
        schedulePublish();
      }, 0);
    };

    // While the keyboard animates, the visual viewport keeps resizing; re-publish the
    // inset each frame, and once it settles re-assert the focused field's position
    // (this is the reliable scroll trigger — it fires after the keyboard stops moving,
    // when the inset is fully grown).
    const onViewportResize = () => {
      schedulePublish();
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (focusedEditable && document.activeElement === focusedEditable) {
          maybeScrollIntoView(focusedEditable);
        }
      }, SETTLE_MS);
    };

    // Orientation change invalidates the rolling layout-height max (portrait max
    // read against landscape height would permanently register as "keyboard open").
    const onOrientationChange = () => {
      maxClientHeight = 0;
      schedulePublish();
    };

    vv.addEventListener('resize', onViewportResize);
    vv.addEventListener('scroll', schedulePublish);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    // Last-resort healing tick: a tap anywhere re-checks the focus/keyboard state.
    // Covers the "keyboard closed via back gesture, then navigated" path where no
    // further viewport resize or focus event would ever fire. rAF-coalesced, so the
    // cost is one publish per interaction burst.
    document.addEventListener('pointerdown', schedulePublish, { capture: true, passive: true });
    window.addEventListener('orientationchange', onOrientationChange);
    publish(); // initialise the var to 0px

    teardown = () => {
      vv.removeEventListener('resize', onViewportResize);
      vv.removeEventListener('scroll', schedulePublish);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('pointerdown', schedulePublish, { capture: true });
      window.removeEventListener('orientationchange', onOrientationChange);
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = null;
      if (blurTimer) clearTimeout(blurTimer);
      blurTimer = null;
      if (anchorTimer) clearTimeout(anchorTimer);
      anchorTimer = null;
      root.style.removeProperty('--keyboard-inset-height');
      root.removeAttribute('data-kb-focus');
      focusedEditable = null;
      keyboardHeight.value = 0;
      isKeyboardOpen.value = false;
      installed = false;
      teardown = null;
    };
    return teardown;
  }

  return {
    install,
    keyboardHeight: readonly(keyboardHeight),
    isKeyboardOpen: readonly(isKeyboardOpen),
  };
}
